import { useState } from 'react'

import { api } from '../api/client'
import type { EagleCI, EaglePunti as Punti, EagleSonda } from '../api/types'
import { t } from '../i18n/lingua'
import { ErrorBanner } from './common'

/**
 * Da dove si osserva un CI (ADR-0022): il punto **principale** legge tutti i
 * parametri, i **secondari** solo la raggiungibilità (ping, porte TCP). Senza
 * scelta decide Eagle: una sonda dedicata che copre l'indirizzo, altrimenti
 * quella che ha scoperto il CI.
 */
export function EaglePunti({
  ciId, punti, puoModificare, onDati,
}: { ciId: string; punti: Punti; puoModificare: boolean; onDati: (d: EagleCI) => void }) {
  const [modifica, setModifica] = useState(false)
  const [sonde, setSonde] = useState<EagleSonda[]>([])
  const [scelti, setScelti] = useState<string[]>([])
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)

  const principale = punti.effective.find((p) => p.role === 'primary')
  const secondari = punti.effective.filter((p) => p.role === 'secondary')

  async function apri() {
    setErrore(null)
    try {
      const r = await api.eagleSonde()
      setSonde(r.probes.filter((s) => s.role !== 'off'))
      setScelti(punti.chosen.map((p) => p.appliance_id))
      setModifica(true)
    } catch (e) {
      setErrore(e)
    }
  }

  async function salva(ids: string[]) {
    setInvio(true)
    setErrore(null)
    try {
      onDati(await api.ciEaglePunti(ciId, ids))
      setModifica(false)
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  const nome = (id: string) => sonde.find((s) => s.id === id)?.name ?? id.slice(0, 8)
  const disponibili = sonde.filter((s) => !scelti.includes(s.id))

  return (
    <div className="eagle-punti">
      <ErrorBanner error={errore} />
      {!modifica ? (
        <p style={{ margin: 0 }}>
          <strong>{t('Osservato da')}</strong>{' '}
          {principale ? (
            <>
              {principale.name} <span className="muted">({punti.pinned && punti.pinned.appliance_id === principale.appliance_id ? t('principale: la sonda che legge con la credenziale') : t('principale')})</span>
              {secondari.map((s) => (
                <span key={s.appliance_id}>, {s.name} <span className="muted">({t('solo raggiungibilità')})</span></span>
              ))}
            </>
          ) : (
            <span className="eagle-motivo">{t('nessuna sonda attiva può leggerlo adesso')}</span>
          )}
          <span className="muted"> · {punti.mode === 'auto' ? t('scelta automatica') : t('scelta manuale')}</span>
          {puoModificare && (
            <>
              {' '}
              <button type="button" className="linklike" onClick={apri}>{t('Cambia')}</button>
            </>
          )}
        </p>
      ) : (
        <div>
          <p className="muted" style={{ marginTop: 0 }}>
            {punti.pinned
              ? t('Questo CI si legge con una credenziale (SNMP, SSH, WinRM): i parametri li legge «{s}», la sonda del job di discovery che l’ha letto con quella credenziale, perché è quella che l’apparato lascia passare; le sonde scelte qui leggono ping e porte, e l’oggetto risulta giù solo se non risponde a nessuna. Per cambiarla, fai girare la discovery con credenziali dalla sonda giusta (in Pianificazioni, «Esegui su»).', { s: punti.pinned.name })
              : t('La prima sonda legge tutti i parametri; le altre solo ping e porte, così l’oggetto risulta giù solo se non risponde a nessuna.')}
          </p>
          {scelti.length === 0 ? (
            <p>{t('Scelta automatica: una sonda dedicata al monitoraggio che copre l’indirizzo, altrimenti quella che ha scoperto il CI.')}</p>
          ) : (
            <ol className="eagle-punti__elenco">
              {scelti.map((id, i) => (
                <li key={id}>
                  {nome(id)}{' '}
                  <span className="muted">{i === 0 ? t('principale') : t('solo raggiungibilità')}</span>{' '}
                  {i > 0 && (
                    <button type="button" className="linklike" aria-label={t('Rendi principale {sonda}', { sonda: nome(id) })}
                            onClick={() => setScelti([id, ...scelti.filter((x) => x !== id)])}>
                      {t('rendi principale')}
                    </button>
                  )}{' '}
                  <button type="button" className="linklike" aria-label={t('Togli {sonda}', { sonda: nome(id) })}
                          onClick={() => setScelti(scelti.filter((x) => x !== id))}>
                    {t('togli')}
                  </button>
                </li>
              ))}
            </ol>
          )}
          {disponibili.length > 0 && scelti.length < 5 && (
            <label className="field" style={{ maxWidth: 320 }}>
              <span className="field__label">{t('Aggiungi una sonda')}</span>
              <select value="" onChange={(e) => e.target.value && setScelti([...scelti, e.target.value])}>
                <option value="">—</option>
                {disponibili.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.role === 'dedicated' ? ` · ${t('dedicata')}` : ''}{s.alive === false ? ` · ${t('non attiva')}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 0 }}>
            <button type="button" onClick={() => salva(scelti)} disabled={invio}>{invio ? t('Salvo…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => salva([])} disabled={invio}>{t('Torna alla scelta automatica')}</button>
            <button type="button" className="secondary" onClick={() => setModifica(false)} disabled={invio}>{t('Annulla')}</button>
          </p>
        </div>
      )}
    </div>
  )
}
